import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import { useTranslation } from "react-i18next";
import { getMyStores, getMyStoreCategories } from "../../api/storeService";
import { getCategories, getCategoryAttributes } from "../../api/categoryService";
import { removeImageBackground } from "../../api/imageService";
import {
  getMyProducts,
  getDeletedProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  restoreProduct,
  deleteProductPermanently,
  uploadProductImage,
  addProductImageFromUrl,
  removeProductImageBackground,
  replaceProductImage,
  deleteProductImage,
  setMainProductImage,
  uploadProductVideo,
  deleteProductVideo,
  createProductVariant,
  updateProductVariant,
  deleteProductVariant,
  uploadVariantImage,
  addVariantImageFromUrl,
  removeVariantImageBackground,
  deleteVariantImage,
  getAuthenticityOptions,
} from "../../api/productService";
import { EmptyState } from "../Admin/analyticsShared";
import { formatCurrency } from "../Admin/constants";

// Numeric values must match the backend's ProductDiscountType enum (Models/Enums.cs).
const DISCOUNT_TYPE_NONE = 0;
const DISCOUNT_TYPE_FIXED = 1;
const DISCOUNT_TYPE_PERCENTAGE = 2;

// Matches the backend's AttributeInputType enum.
const ATTRIBUTE_INPUT_DROPDOWN = 1;
const ATTRIBUTE_INPUT_NUMBER = 3;

// Matches the backend's ProductVideoPlacement enum.
const VIDEO_PLACEMENT_VIDEO_FIRST = 1;
const VIDEO_PLACEMENT_MAIN_THEN_VIDEO = 2;
const VIDEO_PLACEMENT_PHOTOS_FIRST = 3;
const VIDEO_PLACEMENT_OPTIONS = [
  { value: VIDEO_PLACEMENT_VIDEO_FIRST, labelKey: "boProducts:videoPlacementVideoFirst" },
  { value: VIDEO_PLACEMENT_MAIN_THEN_VIDEO, labelKey: "boProducts:videoPlacementMainThenVideo" },
  { value: VIDEO_PLACEMENT_PHOTOS_FIRST, labelKey: "boProducts:videoPlacementPhotosFirst" },
];

const MAX_IMAGES = 8;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const MAX_VIDEO_SIZE_BYTES = 75 * 1024 * 1024;

const emptyVariantForm = {
  variantName: "",
  price: "",
  stockQuantity: "0",
  sku: "",
  discountType: DISCOUNT_TYPE_NONE,
  discountPrice: "",
  discountPercent: "",
  attributeValues: {},
  imageFile: null,
  imageUrl: null,
  imagePreviewUrl: null,
};

// Builds the CreateProductVariantRequest/UpdateProductVariantRequest `attributeValues` list.
// Variant-generating attributes are always dropdowns, so the value is just an option id.
function buildVariantAttributeValuesPayload(variantAttrs, optionIdByAttributeId) {
  return variantAttrs
    .map((attr) => {
      const optionId = optionIdByAttributeId[attr.attributeId];
      return optionId != null ? { attributeId: attr.attributeId, optionId } : null;
    })
    .filter(Boolean);
}

// Products can only be assigned to a leaf category (no subcategories) that the
// store has already linked via its store-categories - mirrors the backend's
// ValidateProductCategoryAsync check.
function buildCategoryOptions(categories, storeCategories) {
  const parentIds = new Set();
  const byId = new Map();
  categories.forEach((c) => {
    byId.set(c.categoryId, c);
    if (c.parentCategoryId != null) parentIds.add(c.parentCategoryId);
  });

  const pathFor = (categoryId) => {
    const parts = [];
    let current = byId.get(categoryId);
    while (current) {
      parts.unshift(current.name);
      current = current.parentCategoryId != null ? byId.get(current.parentCategoryId) : null;
    }
    return parts.join(" › ");
  };

  return storeCategories
    .filter((sc) => !parentIds.has(sc.categoryId))
    .map((sc) => ({ categoryId: sc.categoryId, name: pathFor(sc.categoryId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function resolveDiscountFields(discountType, discountPriceText, discountPercentText, price, t) {
  if (discountType === DISCOUNT_TYPE_FIXED) {
    const parsed = parseFloat(discountPriceText);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed >= price) {
      return { error: t("boProducts:discountPriceError") };
    }
    return { discountType, discountPrice: parsed, discountPercent: null };
  }
  if (discountType === DISCOUNT_TYPE_PERCENTAGE) {
    const parsed = parseFloat(discountPercentText);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed >= 100) {
      return { error: t("boProducts:discountPercentError") };
    }
    return { discountType, discountPrice: null, discountPercent: parsed };
  }
  return { discountType: DISCOUNT_TYPE_NONE, discountPrice: null, discountPercent: null };
}

// Single-select chip row for a dropdown attribute's options (e.g. Size: S/M/L, or
// Color: Black/White/...) - used when building a variant's attribute combination.
function AttributeOptionChips({ options, selectedOptionId, onSelect }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
      {options.map((option) => (
        <Chip
          key={option.optionId}
          label={option.value}
          size="small"
          color={selectedOptionId === option.optionId ? "primary" : "default"}
          variant={selectedOptionId === option.optionId ? "filled" : "outlined"}
          onClick={() => onSelect(selectedOptionId === option.optionId ? null : option.optionId)}
        />
      ))}
    </Stack>
  );
}

// Small dialog for pasting a direct image URL instead of uploading a file - shared
// between the main product photos and a variant's photo.
function AddImageByUrlDialog({ open, onClose, onAdd, busy, error }) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (open) setUrl("");
  }, [open]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("boProducts:addFromLinkTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t("boProducts:addFromLinkLabel")}
            placeholder="https://example.com/photo.jpg"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus
            fullWidth
            disabled={busy}
          />
          <Typography variant="caption" color="text.secondary">
            {t("boProducts:addFromLinkHint")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:cancel")}
        </Button>
        <Button variant="contained" onClick={() => onAdd(url.trim())} disabled={busy || !url.trim()}>
          {busy ? t("boShared:saving") : t("common:add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Products() {
  const { t } = useTranslation(["boProducts", "boSidebar", "boShared", "common", "boStores"]);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(null);
  const [loadingStores, setLoadingStores] = useState(true);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyProductId, setBusyProductId] = useState(null);

  const selectedStore = useMemo(() => stores.find((s) => s.storeId === storeId) || null, [stores, storeId]);

  useEffect(() => {
    let active = true;
    getMyStores()
      .then((data) => {
        if (!active) return;
        setStores(data);
        if (data.length > 0) setStoreId(data[0].storeId);
      })
      .catch(() => {
        if (active) setError(t("boStores:loadError"));
      })
      .finally(() => {
        if (active) setLoadingStores(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reloadProducts() {
    if (!storeId) return;
    setLoadingProducts(true);
    setError("");
    const request = showDeleted ? getDeletedProducts(storeId) : getMyProducts(storeId);
    request
      .then(setProducts)
      .catch(() => setError(t("boProducts:loadError")))
      .finally(() => setLoadingProducts(false));
  }

  useEffect(() => {
    reloadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, showDeleted]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term ? products.filter((p) => p.name.toLowerCase().includes(term)) : products;
    return [...list].sort((a, b) => (a.categoryName || "").localeCompare(b.categoryName || "") || a.name.localeCompare(b.name));
  }, [products, search]);

  function openCreateDialog() {
    setEditingProduct(null);
    setDialogOpen(true);
  }

  function openEditDialog(product) {
    setEditingProduct(product);
    setDialogOpen(true);
  }

  function handleSaved(message) {
    setDialogOpen(false);
    setEditingProduct(null);
    setNotice(message);
    reloadProducts();
  }

  async function handleDelete() {
    const product = deleteTarget;
    setBusyProductId(product.productId);
    try {
      await deleteProduct(product.productId);
      setDeleteTarget(null);
      setNotice(t("boProducts:productDeleted", { name: product.name }));
      reloadProducts();
    } catch {
      setError(t("boProducts:deleteError"));
    } finally {
      setBusyProductId(null);
    }
  }

  async function handleRestore(product) {
    setBusyProductId(product.productId);
    setError("");
    try {
      await restoreProduct(product.productId);
      setNotice(t("boProducts:productRestored", { name: product.name }));
      reloadProducts();
    } catch {
      setError(t("boProducts:restoreError"));
    } finally {
      setBusyProductId(null);
    }
  }

  async function handlePermanentDelete(product) {
    setBusyProductId(product.productId);
    setError("");
    try {
      await deleteProductPermanently(product.productId);
      setNotice(t("boProducts:productPermanentlyDeleted", { name: product.name }));
      reloadProducts();
    } catch (err) {
      setError(err.response?.data || t("boProducts:permanentDeleteError"));
    } finally {
      setBusyProductId(null);
    }
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <Inventory2RoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">{t("boSidebar:products")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("boProducts:subtitle")}
            </Typography>
          </Box>
        </Stack>

        {!showDeleted && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateDialog} disabled={!storeId}>
            {t("boProducts:addProduct")}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : t("boShared:somethingWentWrong")}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2.5, alignItems: { xs: "stretch", sm: "center" } }}>
        {stores.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t("boShared:storeFallback")}</InputLabel>
            <Select label={t("boShared:storeFallback")} value={storeId ?? ""} onChange={(e) => setStoreId(e.target.value)}>
              {stores.map((s) => (
                <MenuItem key={s.storeId} value={s.storeId}>
                  {s.storeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          placeholder={t("boProducts:searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260, flexGrow: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Button
          variant={showDeleted ? "contained" : "outlined"}
          color={showDeleted ? "secondary" : "inherit"}
          startIcon={showDeleted ? <Inventory2RoundedIcon /> : <DeleteOutlineRoundedIcon />}
          onClick={() => setShowDeleted((prev) => !prev)}
        >
          {showDeleted ? t("boProducts:backToProducts") : t("boProducts:deletedProducts")}
        </Button>
      </Stack>

      {loadingStores || loadingProducts ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Skeleton variant="rounded" height={260} />
            </Grid>
          ))}
        </Grid>
      ) : !storeId ? (
        <EmptyState icon={Inventory2RoundedIcon} text={t("boProducts:noStores")} />
      ) : filteredProducts.length === 0 ? (
        <EmptyState
          icon={showDeleted ? DeleteOutlineRoundedIcon : Inventory2RoundedIcon}
          text={showDeleted ? t("boProducts:noDeletedProducts") : t("boProducts:noProducts")}
        />
      ) : (
        <Grid container spacing={2}>
          {filteredProducts.map((product) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={product.productId}>
              <ProductCard
                product={product}
                busy={busyProductId === product.productId}
                showDeleted={showDeleted}
                onEdit={() => openEditDialog(product)}
                onDelete={() => setDeleteTarget(product)}
                onRestore={() => handleRestore(product)}
                onPermanentDelete={() => handlePermanentDelete(product)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {dialogOpen && (
        <ProductFormDialog
          store={selectedStore}
          product={editingProduct}
          onClose={() => {
            setDialogOpen(false);
            setEditingProduct(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "error.main", width: 36, height: 36 }}>
                <WarningAmberRoundedIcon fontSize="small" />
              </Avatar>
              <span>{t("boProducts:deleteConfirmTitle")}</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t("boProducts:deleteConfirmBody", { name: deleteTarget.name })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>{t("common:cancel")}</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              {t("common:delete")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function ProductCard({ product, busy, showDeleted, onEdit, onDelete, onRestore, onPermanentDelete }) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const mainImage = product.images?.find((i) => i.isMain)?.imageUrl || product.images?.[0]?.imageUrl || null;

  return (
    <Card
      variant="outlined"
      sx={{ height: "100%", display: "flex", flexDirection: "column", opacity: busy ? 0.6 : 1, position: "relative" }}
    >
      <Box
        sx={{
          aspectRatio: "1 / 1",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {mainImage ? (
          <Box component="img" src={mainImage} alt={product.name} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <Inventory2RoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        )}
      </Box>

      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
          {product.categoryName && <Chip size="small" label={product.categoryName} variant="outlined" />}
          {product.authenticityValue && (
            <Chip size="small" label={product.authenticityValue} color="primary" variant="outlined" />
          )}
          {!product.isActive && !showDeleted && <Chip size="small" label={t("boShared:inactive")} color="default" />}
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} noWrap title={product.name}>
          {product.name}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5, mb: 0.5 }}>
          {product.discountPrice != null ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                {formatCurrency(product.price)}
              </Typography>
              <Typography variant="subtitle2" color="error.main" fontWeight={700}>
                {formatCurrency(product.discountPrice)}
              </Typography>
            </>
          ) : (
            <Typography variant="subtitle2" fontWeight={700}>
              {formatCurrency(product.price)}
            </Typography>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {product.stockQuantity > 0 ? t("boProducts:inStockCount", { count: product.stockQuantity }) : t("common:outOfStock")}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ my: 1.25 }} />

        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {showDeleted ? (
            <>
              <Tooltip title={t("boProducts:restore")}>
                <IconButton size="small" onClick={onRestore} disabled={busy}>
                  <RestoreFromTrashRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("boProducts:deletePermanently")}>
                <IconButton size="small" color="error" onClick={onPermanentDelete} disabled={busy}>
                  <DeleteForeverRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={t("common:edit")}>
                <IconButton size="small" onClick={onEdit} disabled={busy}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("common:delete")}>
                <IconButton size="small" onClick={onDelete} disabled={busy}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>
    </Card>
  );
}

function ProductFormDialog({ store, product, onClose, onSaved }) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const isEditing = Boolean(product);
  const supportsWholesale = store ? store.type === 2 || store.type === 3 : false;

  const [currentProductId, setCurrentProductId] = useState(product?.productId ?? null);
  const [images, setImages] = useState(product?.images ?? []);
  // Photos/video/variants picked before the product itself is created (no productId
  // to attach them to yet) - uploaded automatically right after the create call succeeds.
  const [pendingImages, setPendingImages] = useState([]);

  const [video, setVideo] = useState(product?.videoUrl ? { videoUrl: product.videoUrl } : null);
  const [pendingVideo, setPendingVideo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [deletingVideo, setDeletingVideo] = useState(false);
  const [videoPlacement, setVideoPlacement] = useState(product?.videoPlacement ?? VIDEO_PLACEMENT_VIDEO_FIRST);

  const [variants, setVariants] = useState(product?.variants ?? []);
  const [pendingVariants, setPendingVariants] = useState([]);
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [editingPendingVariantIndex, setEditingPendingVariantIndex] = useState(null);
  const [variantForm, setVariantForm] = useState(emptyVariantForm);
  const [savingVariant, setSavingVariant] = useState(false);
  const [busyVariantId, setBusyVariantId] = useState(null);
  const [variantImageLinkDialogOpen, setVariantImageLinkDialogOpen] = useState(false);
  const [processingVariantFormBackground, setProcessingVariantFormBackground] = useState(false);

  const [categories, setCategories] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryId, setCategoryId] = useState(product?.categoryId ?? null);

  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributeValues, setAttributeValues] = useState(() => {
    const map = {};
    (product?.attributeValues || []).forEach((av) => {
      map[av.attributeId] = { optionId: av.optionId ?? null, textValue: av.textValue ?? "" };
    });
    return map;
  });

  const [name, setName] = useState(product?.name ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [price, setPrice] = useState(product ? String(product.price) : "");
  const [wholesalePrice, setWholesalePrice] = useState(product?.wholesalePrice != null ? String(product.wholesalePrice) : "");
  const [discountType, setDiscountType] = useState(product?.discountType ?? DISCOUNT_TYPE_NONE);
  const [discountPrice, setDiscountPrice] = useState(product?.discountPrice != null ? String(product.discountPrice) : "");
  const [discountPercent, setDiscountPercent] = useState(product?.discountPercent != null ? String(product.discountPercent) : "");
  const [stockQuantity, setStockQuantity] = useState(product ? String(product.stockQuantity) : "0");
  const [sku, setSku] = useState(product?.sku ?? "");
  const [authenticityOptions, setAuthenticityOptions] = useState([]);
  const [authenticityOptionId, setAuthenticityOptionId] = useState(product?.authenticityOptionId ?? null);
  const [isActive, setIsActive] = useState(product?.isActive ?? true);
  const [requiresSpecialDelivery, setRequiresSpecialDelivery] = useState(product?.requiresSpecialDelivery ?? false);

  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [busyImageId, setBusyImageId] = useState(null);
  const [processingPendingImageIndex, setProcessingPendingImageIndex] = useState(null);
  const [imageLinkDialogOpen, setImageLinkDialogOpen] = useState(false);
  const [addingImageFromLink, setAddingImageFromLink] = useState(false);
  const [imageLinkError, setImageLinkError] = useState("");
  const [formError, setFormError] = useState("");
  const [createdNotice, setCreatedNotice] = useState("");

  const categoryOptions = useMemo(() => buildCategoryOptions(categories, storeCategories), [categories, storeCategories]);
  const descriptiveAttributes = useMemo(
    () => categoryAttributes.filter((a) => !a.isVariant).sort((a, b) => a.displayOrder - b.displayOrder),
    [categoryAttributes],
  );
  const variantAttributes = useMemo(
    () => categoryAttributes.filter((a) => a.isVariant).sort((a, b) => a.displayOrder - b.displayOrder),
    [categoryAttributes],
  );

  useEffect(() => {
    let active = true;
    getAuthenticityOptions()
      .then((options) => {
        if (active) setAuthenticityOptions(options);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!store) return;
    let active = true;
    setLoadingCategories(true);
    Promise.all([getCategories(), getMyStoreCategories(store.storeId)])
      .then(([allCategories, myStoreCategories]) => {
        if (!active) return;
        setCategories(allCategories.filter((c) => c.isActive));
        setStoreCategories(myStoreCategories);
      })
      .catch(() => {
        if (active) setFormError(t("boProducts:loadCategoriesError"));
      })
      .finally(() => {
        if (active) setLoadingCategories(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  useEffect(() => {
    if (categoryOptions.length === 0 || isEditing) return;
    const isValid = categoryOptions.some((c) => c.categoryId === categoryId);
    if (!isValid) setCategoryId(categoryOptions[0].categoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOptions]);

  useEffect(() => {
    if (!categoryId) {
      setCategoryAttributes([]);
      return;
    }
    let active = true;
    setLoadingAttributes(true);
    getCategoryAttributes(categoryId)
      .then((data) => {
        if (active) setCategoryAttributes(data);
      })
      .catch(() => {
        if (active) setCategoryAttributes([]);
      })
      .finally(() => {
        if (active) setLoadingAttributes(false);
      });
    return () => {
      active = false;
    };
  }, [categoryId]);

  function setAttributeOptionValue(attributeId, optionId) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId, textValue: "" } }));
  }

  function setAttributeTextValue(attributeId, textValue) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId: null, textValue } }));
  }

  function buildAttributeValuesPayload() {
    return descriptiveAttributes
      .map((attr) => {
        const value = attributeValues[attr.attributeId];
        if (!value) return null;
        if (attr.inputType === ATTRIBUTE_INPUT_DROPDOWN) {
          return value.optionId != null ? { attributeId: attr.attributeId, optionId: value.optionId } : null;
        }
        const trimmed = (value.textValue || "").trim();
        return trimmed ? { attributeId: attr.attributeId, textValue: trimmed } : null;
      })
      .filter(Boolean);
  }

  async function handleSubmit() {
    const trimmedName = name.trim();
    const parsedPrice = parseFloat(price);

    if (!categoryId) {
      setFormError(t("boProducts:chooseCategoryError"));
      return;
    }
    if (!trimmedName) {
      setFormError(t("boProducts:enterNameError"));
      return;
    }
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setFormError(t("boProducts:invalidPriceError"));
      return;
    }

    let parsedWholesalePrice = null;
    if (supportsWholesale && wholesalePrice.trim() !== "") {
      parsedWholesalePrice = parseFloat(wholesalePrice);
      if (Number.isNaN(parsedWholesalePrice) || parsedWholesalePrice < 0) {
        setFormError(t("boProducts:invalidWholesalePriceError"));
        return;
      }
    }

    const discountResult = resolveDiscountFields(discountType, discountPrice, discountPercent, parsedPrice, t);
    if (discountResult.error) {
      setFormError(discountResult.error);
      return;
    }

    const attributeValuesPayload = buildAttributeValuesPayload();
    const missingRequired = descriptiveAttributes.filter(
      (a) => a.isRequired && !attributeValuesPayload.some((v) => v.attributeId === a.attributeId),
    );
    if (missingRequired.length > 0) {
      setFormError(t("boProducts:missingAttributes", { list: missingRequired.map((a) => a.attributeName).join(", ") }));
      return;
    }

    setFormError("");
    setSaving(true);

    const payload = {
      categoryId,
      name: trimmedName,
      description: description.trim() || null,
      price: parsedPrice,
      wholesalePrice: parsedWholesalePrice,
      discountType: discountResult.discountType,
      discountPrice: discountResult.discountPrice,
      discountPercent: discountResult.discountPercent,
      stockQuantity: parseInt(stockQuantity, 10) || 0,
      sku: sku.trim() || null,
      authenticityOptionId,
      requiresSpecialDelivery,
      attributeValues: attributeValuesPayload,
    };

    try {
      if (isEditing) {
        await updateProduct(currentProductId, { ...payload, isActive, videoPlacement });
        onSaved(t("boProducts:productUpdated", { name: trimmedName }));
      } else if (currentProductId) {
        // Product was already created in this dialog session (e.g. photos were added); this
        // submit just updates the rest of the fields.
        await updateProduct(currentProductId, { ...payload, isActive: true, videoPlacement });
        onSaved(t("boProducts:productUpdated", { name: trimmedName }));
      } else {
        const created = await createProduct({ ...payload, storeId: store.storeId });
        setCurrentProductId(created.productId);
        setImages(created.images || []);

        if (pendingImages.length > 0) {
          await uploadPendingImages(created.productId);
        }
        if (pendingVideo) {
          await uploadPendingVideo(created.productId);
        }
        if (pendingVariants.length > 0) {
          await uploadPendingVariants(created.productId);
        }
        setCreatedNotice(t("boProducts:productCreatedNotice"));
      }
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:saveError"));
    } finally {
      setSaving(false);
    }
  }

  function validateImageFile(file) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setFormError(t("boShared:invalidImageType"));
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setFormError(t("boProducts:imageTooLarge"));
      return false;
    }
    return true;
  }

  async function handleAddImage(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const remaining = MAX_IMAGES - (images.length + pendingImages.length);
    const validFiles = files.slice(0, remaining).filter(validateImageFile);
    if (validFiles.length === 0) return;

    if (!currentProductId) {
      setFormError("");
      setPendingImages((prev) => [
        ...prev,
        ...validFiles.map((file) => ({ file, previewUrl: URL.createObjectURL(file) })),
      ]);
      return;
    }

    setFormError("");
    setUploadingImage(true);
    try {
      for (const file of validFiles) {
        // eslint-disable-next-line no-await-in-loop
        const updated = await uploadProductImage(currentProductId, file);
        setImages(updated.images);
      }
    } catch {
      setFormError(t("boProducts:uploadPhotoError"));
    } finally {
      setUploadingImage(false);
    }
  }

  function removePendingImage(index) {
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
  }

  async function uploadPendingImages(targetProductId) {
    for (const pending of pendingImages) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const updated = pending.file
          ? await uploadProductImage(targetProductId, pending.file)
          : await addProductImageFromUrl(targetProductId, pending.url);
        setImages(updated.images);
      } catch {
        setFormError(t("boProducts:someImagesFailedUpload"));
      }
    }
    setPendingImages([]);
  }

  async function handleAddImageFromUrl(url) {
    if (!url) return;
    setImageLinkError("");

    if (!currentProductId) {
      const remaining = MAX_IMAGES - (images.length + pendingImages.length);
      if (remaining <= 0) return;
      setPendingImages((prev) => [...prev, { url, previewUrl: url }]);
      setImageLinkDialogOpen(false);
      return;
    }

    setAddingImageFromLink(true);
    try {
      const updated = await addProductImageFromUrl(currentProductId, url);
      setImages(updated.images);
      setImageLinkDialogOpen(false);
    } catch (err) {
      const data = err.response?.data;
      setImageLinkError(typeof data === "string" && data ? data : t("boProducts:addFromLinkError"));
    } finally {
      setAddingImageFromLink(false);
    }
  }

  async function handleRemoveImageBackground(imageId) {
    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await removeProductImageBackground(currentProductId, imageId);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleRemovePendingImageBackground(index) {
    const pending = pendingImages[index];
    if (!pending?.file) return;

    setFormError("");
    setProcessingPendingImageIndex(index);
    try {
      const blob = await removeImageBackground(pending.file);
      const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
      setPendingImages((prev) =>
        prev.map((p, i) => (i === index ? { file: resultFile, previewUrl: URL.createObjectURL(resultFile) } : p)),
      );
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setProcessingPendingImageIndex(null);
    }
  }

  async function handleReplaceImage(imageId, e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validateImageFile(file)) return;

    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await replaceProductImage(currentProductId, imageId, file);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:replacePhotoError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleDeleteImage(imageId) {
    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await deleteProductImage(currentProductId, imageId);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:removePhotoError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleSetMainImage(imageId) {
    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await setMainProductImage(currentProductId, imageId);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:setMainPhotoError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleAddVideo(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      setFormError(t("boProducts:invalidVideoType"));
      return;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      setFormError(t("boProducts:videoTooLarge"));
      return;
    }

    setFormError("");

    if (!currentProductId) {
      setPendingVideo({ file, previewUrl: URL.createObjectURL(file) });
      return;
    }

    setUploadingVideo(true);
    try {
      const updated = await uploadProductVideo(currentProductId, file);
      setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:uploadVideoError"));
    } finally {
      setUploadingVideo(false);
    }
  }

  async function uploadPendingVideo(targetProductId) {
    if (!pendingVideo) return;
    try {
      const updated = await uploadProductVideo(targetProductId, pendingVideo.file);
      setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:uploadVideoError"));
    }
    setPendingVideo(null);
  }

  async function handleDeleteVideo() {
    setFormError("");
    setDeletingVideo(true);
    try {
      const updated = await deleteProductVideo(currentProductId);
      setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
    } catch {
      setFormError(t("boProducts:removeVideoError"));
    } finally {
      setDeletingVideo(false);
    }
  }

  function openAddVariant() {
    setEditingVariantId(null);
    setEditingPendingVariantIndex(null);
    setVariantForm(emptyVariantForm);
    setShowVariantForm(true);
  }

  function openEditVariant(variant) {
    setEditingVariantId(variant.variantId);
    setEditingPendingVariantIndex(null);
    const attributeValuesMap = {};
    (variant.attributeValues || []).forEach((av) => {
      attributeValuesMap[av.attributeId] = av.optionId;
    });
    setVariantForm({
      variantName: variant.variantName,
      price: String(variant.price),
      stockQuantity: String(variant.stockQuantity),
      sku: variant.sku || "",
      discountType: variant.discountType ?? DISCOUNT_TYPE_NONE,
      discountPrice: variant.discountPrice != null ? String(variant.discountPrice) : "",
      discountPercent: variant.discountPercent != null ? String(variant.discountPercent) : "",
      attributeValues: attributeValuesMap,
      imageFile: null,
      imageUrl: null,
      imagePreviewUrl: variant.imageUrl || null,
    });
    setShowVariantForm(true);
  }

  // A variant staged locally before the product itself has been created (no
  // productId yet to attach it to) - mirrors pendingImages/pendingVideo.
  function openEditPendingVariant(index) {
    const pending = pendingVariants[index];
    setEditingVariantId(null);
    setEditingPendingVariantIndex(index);
    const attributeValuesMap = {};
    (pending.attributeValues || []).forEach((av) => {
      attributeValuesMap[av.attributeId] = av.optionId;
    });
    setVariantForm({
      variantName: pending.variantName || "",
      price: String(pending.price),
      stockQuantity: String(pending.stockQuantity),
      sku: pending.sku || "",
      discountType: pending.discountType ?? DISCOUNT_TYPE_NONE,
      discountPrice: pending.discountPrice != null ? String(pending.discountPrice) : "",
      discountPercent: pending.discountPercent != null ? String(pending.discountPercent) : "",
      attributeValues: attributeValuesMap,
      imageFile: pending.imageFile,
      imageUrl: pending.imageUrl,
      imagePreviewUrl: pending.imagePreviewUrl,
    });
    setShowVariantForm(true);
  }

  function removePendingVariant(index) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("boProducts:removeVariantConfirm"))) return;
    setPendingVariants((prev) => prev.filter((_, i) => i !== index));
  }

  function cancelVariantForm() {
    setShowVariantForm(false);
    setEditingVariantId(null);
    setEditingPendingVariantIndex(null);
    setVariantForm(emptyVariantForm);
  }

  function setVariantAttributeOption(attributeId, optionId) {
    setVariantForm((prev) => ({ ...prev, attributeValues: { ...prev.attributeValues, [attributeId]: optionId } }));
  }

  function handleVariantImageFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validateImageFile(file)) return;

    if (editingVariantId) {
      handleUploadVariantImage(editingVariantId, file);
      return;
    }
    setVariantForm((prev) => ({ ...prev, imageFile: file, imageUrl: null, imagePreviewUrl: URL.createObjectURL(file) }));
  }

  function handleVariantImageFromUrl(url) {
    if (!url) return;
    if (editingVariantId) {
      handleAddVariantImageFromUrl(editingVariantId, url);
      setVariantImageLinkDialogOpen(false);
      return;
    }
    setVariantForm((prev) => ({ ...prev, imageFile: null, imageUrl: url, imagePreviewUrl: url }));
    setVariantImageLinkDialogOpen(false);
  }

  async function handleUploadVariantImage(variantId, file) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await uploadVariantImage(currentProductId, variantId, file);
      setVariants(updated.variants);
      const savedVariant = updated.variants.find((v) => v.variantId === variantId);
      setVariantForm((prev) => ({ ...prev, imagePreviewUrl: savedVariant?.imageUrl ?? prev.imagePreviewUrl }));
    } catch {
      setFormError(t("boProducts:uploadVariantPhotoError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function handleAddVariantImageFromUrl(variantId, url) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await addVariantImageFromUrl(currentProductId, variantId, url);
      setVariants(updated.variants);
      const savedVariant = updated.variants.find((v) => v.variantId === variantId);
      setVariantForm((prev) => ({ ...prev, imagePreviewUrl: savedVariant?.imageUrl ?? prev.imagePreviewUrl }));
    } catch {
      setFormError(t("boProducts:addFromLinkError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function handleRemoveVariantImageBackground(variantId) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await removeVariantImageBackground(currentProductId, variantId);
      setVariants(updated.variants);
      const savedVariant = updated.variants.find((v) => v.variantId === variantId);
      if (editingVariantId === variantId) {
        setVariantForm((prev) => ({ ...prev, imagePreviewUrl: savedVariant?.imageUrl ?? prev.imagePreviewUrl }));
      }
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function handleRemoveVariantFormImageBackground() {
    setFormError("");
    if (variantForm.imageFile) {
      setProcessingVariantFormBackground(true);
      try {
        const blob = await removeImageBackground(variantForm.imageFile);
        const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
        setVariantForm((prev) => ({ ...prev, imageFile: resultFile, imageUrl: null, imagePreviewUrl: URL.createObjectURL(resultFile) }));
      } catch {
        setFormError(t("boProducts:removeBackgroundError"));
      } finally {
        setProcessingVariantFormBackground(false);
      }
      return;
    }
    if (editingVariantId) {
      await handleRemoveVariantImageBackground(editingVariantId);
    }
  }

  async function handleDeleteVariantImage(variantId) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await deleteVariantImage(currentProductId, variantId);
      setVariants(updated.variants);
    } catch {
      setFormError(t("boProducts:removeVariantPhotoError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function saveVariant() {
    const trimmedName = variantForm.variantName.trim();
    const parsedPrice = parseFloat(variantForm.price);
    const variantAttributeValuesPayload = buildVariantAttributeValuesPayload(
      variantAttributes,
      variantForm.attributeValues,
    );

    if (variantAttributes.length > 0 && variantAttributeValuesPayload.length !== variantAttributes.length) {
      setFormError(
        t("boProducts:missingVariantAttributes", {
          attributes: variantAttributes.map((a) => a.attributeName).join(", "),
        }),
      );
      return;
    }
    if (!trimmedName && variantAttributeValuesPayload.length === 0) {
      setFormError(t("boProducts:missingVariantName"));
      return;
    }
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setFormError(t("boProducts:invalidVariantPrice"));
      return;
    }

    const discountResult = resolveDiscountFields(
      variantForm.discountType,
      variantForm.discountPrice,
      variantForm.discountPercent,
      parsedPrice,
      t,
    );
    if (discountResult.error) {
      setFormError(discountResult.error);
      return;
    }

    setFormError("");

    const payload = {
      variantName: trimmedName || null,
      price: parsedPrice,
      discountType: discountResult.discountType,
      discountPrice: discountResult.discountPrice,
      discountPercent: discountResult.discountPercent,
      stockQuantity: parseInt(variantForm.stockQuantity, 10) || 0,
      sku: variantForm.sku.trim() || null,
      attributeValues: variantAttributeValuesPayload,
    };

    // The product doesn't exist yet, so there's no productId to attach a variant
    // to - stage it locally and create it via uploadPendingVariants() right after
    // the product itself is created (same pattern as pendingImages/pendingVideo).
    if (!currentProductId) {
      setPendingVariants((prev) => {
        const entry = {
          ...payload,
          imageFile: variantForm.imageFile,
          imageUrl: variantForm.imageUrl,
          imagePreviewUrl: variantForm.imagePreviewUrl,
        };
        if (editingPendingVariantIndex != null) {
          const copy = [...prev];
          copy[editingPendingVariantIndex] = entry;
          return copy;
        }
        return [...prev, entry];
      });
      cancelVariantForm();
      return;
    }

    setSavingVariant(true);

    try {
      const previousVariantIds = new Set(variants.map((v) => v.variantId));
      const updated = editingVariantId
        ? await updateProductVariant(currentProductId, editingVariantId, { ...payload, isActive: true })
        : await createProductVariant(currentProductId, payload);
      setVariants(updated.variants);

      if (!editingVariantId && (variantForm.imageFile || variantForm.imageUrl)) {
        const createdVariant = updated.variants.find((v) => !previousVariantIds.has(v.variantId));
        if (createdVariant) {
          if (variantForm.imageFile) {
            await handleUploadVariantImage(createdVariant.variantId, variantForm.imageFile);
          } else {
            await handleAddVariantImageFromUrl(createdVariant.variantId, variantForm.imageUrl);
          }
        }
      }

      cancelVariantForm();
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:saveVariantError"));
    } finally {
      setSavingVariant(false);
    }
  }

  async function uploadPendingVariants(targetProductId) {
    let currentVariants = [];
    for (const pending of pendingVariants) {
      try {
        const previousIds = new Set(currentVariants.map((v) => v.variantId));
        const { imageFile, imageUrl, imagePreviewUrl, ...variantPayload } = pending;
        // eslint-disable-next-line no-await-in-loop
        const updated = await createProductVariant(targetProductId, variantPayload);
        currentVariants = updated.variants;
        setVariants(currentVariants);

        if (imageFile || imageUrl) {
          const createdVariant = currentVariants.find((v) => !previousIds.has(v.variantId));
          if (createdVariant) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const withImage = imageFile
                ? await uploadVariantImage(targetProductId, createdVariant.variantId, imageFile)
                : await addVariantImageFromUrl(targetProductId, createdVariant.variantId, imageUrl);
              currentVariants = withImage.variants;
              setVariants(currentVariants);
            } catch {
              setFormError(t("boProducts:uploadVariantPhotoError"));
            }
          }
        }
      } catch {
        setFormError(t("boProducts:saveVariantError"));
      }
    }
    setPendingVariants([]);
  }

  function confirmDeleteVariant(variantId) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("boProducts:removeVariantConfirm"))) return;
    handleDeleteVariant(variantId);
  }

  async function handleDeleteVariant(variantId) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await deleteProductVariant(currentProductId, variantId);
      setVariants(updated.variants);
    } catch {
      setFormError(t("boProducts:removeVariantError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle>{isEditing ? t("boProducts:editProductTitle", { name: product.name }) : t("boProducts:newProductTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {formError && (
            <Alert severity="error" onClose={() => setFormError("")}>
              {formError}
            </Alert>
          )}
          {createdNotice && (
            <Alert severity="success" onClose={() => setCreatedNotice("")}>
              {createdNotice}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("boProducts:photos")}
            </Typography>
            <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap", gap: 1.25 }}>
                {!currentProductId &&
                  pendingImages.map((pending, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        width: 84,
                        height: 84,
                        borderRadius: 1.5,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box component="img" src={pending.previewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      {index === 0 && (
                        <Box sx={{ position: "absolute", top: 2, left: 2, bgcolor: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: 0.75, px: 0.6 }}>
                          <Typography variant="caption" sx={{ fontSize: 10 }}>
                            {t("boProducts:mainPhoto")}
                          </Typography>
                        </Box>
                      )}
                      {processingPendingImageIndex === index && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0,0,0,0.45)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        </Box>
                      )}
                      <Stack direction="row" spacing={0.25} sx={{ position: "absolute", top: 2, right: 2 }}>
                        {pending.file && (
                          <Tooltip title={t("boProducts:removeBackground")}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemovePendingImageBackground(index)}
                              disabled={processingPendingImageIndex === index}
                              sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                            >
                              <AutoFixHighRoundedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => removePendingImage(index)}
                          disabled={processingPendingImageIndex === index}
                          sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Box>
                  ))}

                {currentProductId &&
                  images.map((img) => (
                  <Box
                    key={img.imageId}
                    sx={{
                      position: "relative",
                      width: 84,
                      height: 84,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box component="img" src={img.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    {busyImageId === img.imageId && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(0,0,0,0.45)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                      </Box>
                    )}
                    <Stack direction="row" spacing={0.25} sx={{ position: "absolute", top: 2, right: 2 }}>
                      <Tooltip title={img.isMain ? t("boProducts:mainPhoto") : t("boProducts:setAsMain")}>
                        <IconButton
                          size="small"
                          onClick={() => !img.isMain && handleSetMainImage(img.imageId)}
                          sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                        >
                          {img.isMain ? <StarRoundedIcon sx={{ fontSize: 14 }} /> : <StarOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("boProducts:removeBackground")}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            handleRemoveImageBackground(img.imageId);
                          }}
                          sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                        >
                          <AutoFixHighRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteImage(img.imageId)}
                        sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                    <Box component="label" sx={{ position: "absolute", inset: 0, cursor: "pointer" }}>
                      <input type="file" accept="image/*" hidden onChange={(e) => handleReplaceImage(img.imageId, e)} />
                    </Box>
                  </Box>
                ))}

                {images.length + pendingImages.length < MAX_IMAGES && (
                  <Box
                    component="label"
                    sx={{
                      width: 84,
                      height: 84,
                      borderRadius: 1.5,
                      border: "1.5px dashed",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexDirection: "column",
                    }}
                  >
                    {uploadingImage ? (
                      <CircularProgress size={18} />
                    ) : (
                      <>
                        <CloudUploadOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                          {t("common:add")}
                        </Typography>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      multiple={!currentProductId}
                      hidden
                      onChange={handleAddImage}
                      disabled={uploadingImage}
                    />
                  </Box>
                )}

                {images.length + pendingImages.length < MAX_IMAGES && (
                  <Box
                    onClick={() => setImageLinkDialogOpen(true)}
                    sx={{
                      width: 84,
                      height: 84,
                      borderRadius: 1.5,
                      border: "1.5px dashed",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexDirection: "column",
                    }}
                  >
                    <InsertLinkRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", px: 0.5 }}>
                      {t("boProducts:addFromLink")}
                    </Typography>
                  </Box>
                )}
            </Stack>
          </Box>

          <AddImageByUrlDialog
            open={imageLinkDialogOpen}
            onClose={() => setImageLinkDialogOpen(false)}
            onAdd={handleAddImageFromUrl}
            busy={addingImageFromLink}
            error={imageLinkError}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("boProducts:video")}
            </Typography>
            {images.length + pendingImages.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {t("boProducts:addPhotoBeforeVideoHint")}
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {video || pendingVideo ? (
                  <Box
                    sx={{
                      position: "relative",
                      width: 160,
                      height: 160,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.default",
                    }}
                  >
                    <Box
                      component="video"
                      src={video ? video.videoUrl : pendingVideo.previewUrl}
                      controls
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    {deletingVideo ? (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(0,0,0,0.45)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                      </Box>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={video ? handleDeleteVideo : () => setPendingVideo(null)}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          p: 0.4,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Box
                    component="label"
                    sx={{
                      width: 160,
                      height: 84,
                      borderRadius: 1.5,
                      border: "1.5px dashed",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexDirection: "column",
                    }}
                  >
                    {uploadingVideo ? (
                      <CircularProgress size={18} />
                    ) : (
                      <>
                        <VideocamOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                          {t("boProducts:addVideo")}
                        </Typography>
                      </>
                    )}
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime"
                      hidden
                      onChange={handleAddVideo}
                      disabled={uploadingVideo}
                    />
                  </Box>
                )}

                {isEditing && video && (
                  <FormControl size="small">
                    <RadioGroup
                      value={videoPlacement}
                      onChange={(e) => setVideoPlacement(Number(e.target.value))}
                    >
                      {VIDEO_PLACEMENT_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio size="small" />}
                          label={t(option.labelKey)}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              </Stack>
            )}
          </Box>

          <FormControl size="small" fullWidth disabled={loadingCategories || categoryOptions.length === 0}>
            <InputLabel>{t("boProducts:category")}</InputLabel>
            <Select label={t("boProducts:category")} value={categoryId ?? ""} onChange={(e) => setCategoryId(e.target.value)}>
              {categoryOptions.map((c) => (
                <MenuItem key={c.categoryId} value={c.categoryId}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            {!loadingCategories && categoryOptions.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("boProducts:noCategoriesHint")}
              </Typography>
            )}
          </FormControl>

          <TextField label={t("boProducts:name")} value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
          <TextField
            label={t("boShared:description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={t("common:price")}
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              fullWidth
              slotProps={{ input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> } }}
            />
            <TextField
              label={t("boProducts:stockQuantity")}
              type="number"
              value={stockQuantity}
              onChange={(e) => setStockQuantity(e.target.value)}
              fullWidth
            />
          </Stack>

          {supportsWholesale && (
            <TextField
              label={t("boProducts:wholesalePriceOptional")}
              type="number"
              value={wholesalePrice}
              onChange={(e) => setWholesalePrice(e.target.value)}
              fullWidth
              slotProps={{ input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> } }}
            />
          )}

          <TextField label={t("boProducts:skuOptional")} value={sku} onChange={(e) => setSku(e.target.value)} fullWidth />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t("boProducts:authenticity")}
            </Typography>
            <RadioGroup
              row
              value={authenticityOptionId ?? ""}
              onChange={(e) => setAuthenticityOptionId(e.target.value === "" ? null : Number(e.target.value))}
            >
              <FormControlLabel value="" control={<Radio size="small" />} label={t("boProducts:notSpecified")} />
              {authenticityOptions.map((option) => (
                <FormControlLabel
                  key={option.authenticityOptionId}
                  value={option.authenticityOptionId}
                  control={<Radio size="small" />}
                  label={option.value}
                />
              ))}
            </RadioGroup>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t("boShared:discount")}
            </Typography>
            <RadioGroup
              row
              value={discountType}
              onChange={(e) => setDiscountType(Number(e.target.value))}
            >
              <FormControlLabel value={DISCOUNT_TYPE_NONE} control={<Radio size="small" />} label={t("boProducts:discountNone")} />
              <FormControlLabel value={DISCOUNT_TYPE_FIXED} control={<Radio size="small" />} label={t("boProducts:discountFixed")} />
              <FormControlLabel value={DISCOUNT_TYPE_PERCENTAGE} control={<Radio size="small" />} label={t("boProducts:discountPercentage")} />
            </RadioGroup>
            {discountType === DISCOUNT_TYPE_FIXED && (
              <TextField
                label={t("boProducts:salePrice")}
                type="number"
                value={discountPrice}
                onChange={(e) => setDiscountPrice(e.target.value)}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
              />
            )}
            {discountType === DISCOUNT_TYPE_PERCENTAGE && (
              <TextField
                label={t("boProducts:discountPercentLabel")}
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          {loadingAttributes ? (
            <Skeleton variant="rounded" height={40} />
          ) : (
            descriptiveAttributes.length > 0 && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">{t("boProducts:categoryAttributes")}</Typography>
                {descriptiveAttributes.map((attr) => (
                  <Box key={attr.attributeId}>
                    {attr.inputType === ATTRIBUTE_INPUT_DROPDOWN ? (
                      <FormControl size="small" fullWidth>
                        <InputLabel>
                          {attr.attributeName}
                          {attr.isRequired ? " *" : ""}
                        </InputLabel>
                        <Select
                          label={`${attr.attributeName}${attr.isRequired ? " *" : ""}`}
                          value={attributeValues[attr.attributeId]?.optionId ?? ""}
                          onChange={(e) => setAttributeOptionValue(attr.attributeId, e.target.value)}
                        >
                          {attr.options.map((opt) => (
                            <MenuItem key={opt.optionId} value={opt.optionId}>
                              {opt.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        label={`${attr.attributeName}${attr.unit ? ` (${attr.unit})` : ""}${attr.isRequired ? " *" : ""}`}
                        type={attr.inputType === ATTRIBUTE_INPUT_NUMBER ? "number" : "text"}
                        value={attributeValues[attr.attributeId]?.textValue ?? ""}
                        onChange={(e) => setAttributeTextValue(attr.attributeId, e.target.value)}
                        size="small"
                        fullWidth
                      />
                    )}
                  </Box>
                ))}
              </Stack>
            )
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "action.hover" }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("boProducts:requiresSpecialDelivery")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("boProducts:specialDeliveryHint")}
              </Typography>
            </Box>
            <Switch
              checked={requiresSpecialDelivery}
              onChange={(e) => setRequiresSpecialDelivery(e.target.checked)}
            />
          </Stack>

          {isEditing && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "action.hover" }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("boShared:active")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("boProducts:inactiveHint")}
                </Typography>
              </Box>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Stack>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("boProducts:variants")}
            </Typography>
            <Stack spacing={1.25}>
                {variants.map((v) => (
                  <Stack
                    key={v.variantId}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ p: 1, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        overflow: "hidden",
                        flexShrink: 0,
                        bgcolor: "background.default",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {busyVariantId === v.variantId ? (
                        <CircularProgress size={16} />
                      ) : v.imageUrl ? (
                        <Box component="img" src={v.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={v.variantName}>
                        {v.variantName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.discountPrice != null ? (
                          <>
                            <Box component="span" sx={{ textDecoration: "line-through", mr: 0.5 }}>
                              {formatCurrency(v.price)}
                            </Box>
                            {formatCurrency(v.discountPrice)}
                          </>
                        ) : (
                          formatCurrency(v.price)
                        )}
                        {" · "}
                        {t("boProducts:inStockCount", { count: v.stockQuantity })}
                        {v.sku ? ` · ${v.sku}` : ""}
                        {!v.isActive ? ` · ${t("boProducts:variantInactive")}` : ""}
                      </Typography>
                    </Box>
                    {v.imageUrl && (
                      <Tooltip title={t("boProducts:removeBackground")}>
                        <IconButton size="small" onClick={() => handleRemoveVariantImageBackground(v.variantId)} disabled={busyVariantId === v.variantId}>
                          <AutoFixHighRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {v.imageUrl && (
                      <Tooltip title={t("boProducts:removeVariantPhoto")}>
                        <IconButton size="small" onClick={() => handleDeleteVariantImage(v.variantId)} disabled={busyVariantId === v.variantId}>
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t("common:edit")}>
                      <IconButton size="small" onClick={() => openEditVariant(v)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common:delete")}>
                      <IconButton size="small" onClick={() => confirmDeleteVariant(v.variantId)} disabled={busyVariantId === v.variantId}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}

                {pendingVariants.map((pv, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ p: 1, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        overflow: "hidden",
                        flexShrink: 0,
                        bgcolor: "background.default",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {pv.imagePreviewUrl ? (
                        <Box component="img" src={pv.imagePreviewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={pv.variantName || t("boProducts:variantName")}>
                        {pv.variantName || t("boProducts:variantName")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pv.discountPrice != null ? (
                          <>
                            <Box component="span" sx={{ textDecoration: "line-through", mr: 0.5 }}>
                              {formatCurrency(pv.price)}
                            </Box>
                            {formatCurrency(pv.discountPrice)}
                          </>
                        ) : (
                          formatCurrency(pv.price)
                        )}
                        {" · "}
                        {t("boProducts:inStockCount", { count: pv.stockQuantity })}
                        {pv.sku ? ` · ${pv.sku}` : ""}
                      </Typography>
                    </Box>
                    <Tooltip title={t("common:edit")}>
                      <IconButton size="small" onClick={() => openEditPendingVariant(index)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common:delete")}>
                      <IconButton size="small" onClick={() => removePendingVariant(index)}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}

                {showVariantForm ? (
                  <Stack spacing={1.5} sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={{ position: "relative", width: 64, height: 64, flexShrink: 0 }}>
                        <Box
                          component="label"
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 1.5,
                            border: "1.5px dashed",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            overflow: "hidden",
                          }}
                        >
                          {variantForm.imagePreviewUrl ? (
                            <Box component="img" src={variantForm.imagePreviewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <PhotoCameraOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                          )}
                          <input type="file" accept="image/*" hidden onChange={handleVariantImageFile} />
                        </Box>
                        {(processingVariantFormBackground || (editingVariantId != null && busyVariantId === editingVariantId)) && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: 1.5,
                              bgcolor: "rgba(0,0,0,0.45)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                          </Box>
                        )}
                        <Stack direction="row" spacing={0.25} sx={{ position: "absolute", top: 2, right: 2 }}>
                          <Tooltip title={t("boProducts:addFromLink")}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                setVariantImageLinkDialogOpen(true);
                              }}
                              sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                            >
                              <InsertLinkRoundedIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                          {variantForm.imagePreviewUrl && (
                            <Tooltip title={t("boProducts:removeBackground")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRemoveVariantFormImageBackground();
                                }}
                                disabled={processingVariantFormBackground || (editingVariantId != null && busyVariantId === editingVariantId)}
                                sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                              >
                                <AutoFixHighRoundedIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>

                      <Stack spacing={1} sx={{ flexGrow: 1 }}>
                        {variantAttributes.map((attr) => (
                          <Box key={attr.attributeId}>
                            <Typography variant="caption" color="text.secondary">
                              {attr.attributeName}
                              {attr.unit ? ` (${attr.unit})` : ""} *
                            </Typography>
                            <AttributeOptionChips
                              options={attr.options}
                              selectedOptionId={variantForm.attributeValues[attr.attributeId] ?? null}
                              onSelect={(optionId) => setVariantAttributeOption(attr.attributeId, optionId)}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Stack>

                    <TextField
                      label={
                        variantAttributes.length > 0
                          ? t("boProducts:variantNameOptionalAuto")
                          : t("boProducts:variantName")
                      }
                      value={variantForm.variantName}
                      onChange={(e) => setVariantForm((prev) => ({ ...prev, variantName: e.target.value }))}
                      size="small"
                      fullWidth
                    />

                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        label={t("common:price")}
                        type="number"
                        value={variantForm.price}
                        onChange={(e) => setVariantForm((prev) => ({ ...prev, price: e.target.value }))}
                        size="small"
                        fullWidth
                        slotProps={{ input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> } }}
                      />
                      <TextField
                        label={t("boProducts:stockQuantity")}
                        type="number"
                        value={variantForm.stockQuantity}
                        onChange={(e) => setVariantForm((prev) => ({ ...prev, stockQuantity: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    </Stack>

                    <Box>
                      <RadioGroup
                        row
                        value={variantForm.discountType}
                        onChange={(e) => setVariantForm((prev) => ({ ...prev, discountType: Number(e.target.value) }))}
                      >
                        <FormControlLabel value={DISCOUNT_TYPE_NONE} control={<Radio size="small" />} label={t("boProducts:discountNone")} />
                        <FormControlLabel value={DISCOUNT_TYPE_FIXED} control={<Radio size="small" />} label={t("boProducts:discountFixed")} />
                        <FormControlLabel value={DISCOUNT_TYPE_PERCENTAGE} control={<Radio size="small" />} label={t("boProducts:discountPercentage")} />
                      </RadioGroup>
                      {variantForm.discountType === DISCOUNT_TYPE_FIXED && (
                        <TextField
                          label={t("boProducts:salePrice")}
                          type="number"
                          value={variantForm.discountPrice}
                          onChange={(e) => setVariantForm((prev) => ({ ...prev, discountPrice: e.target.value }))}
                          size="small"
                          fullWidth
                        />
                      )}
                      {variantForm.discountType === DISCOUNT_TYPE_PERCENTAGE && (
                        <TextField
                          label={t("boProducts:discountPercentLabel")}
                          type="number"
                          value={variantForm.discountPercent}
                          onChange={(e) => setVariantForm((prev) => ({ ...prev, discountPercent: e.target.value }))}
                          size="small"
                          fullWidth
                        />
                      )}
                    </Box>

                    <TextField
                      label={t("boProducts:skuOptional")}
                      value={variantForm.sku}
                      onChange={(e) => setVariantForm((prev) => ({ ...prev, sku: e.target.value }))}
                      size="small"
                      fullWidth
                    />

                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                      <Button onClick={cancelVariantForm} disabled={savingVariant}>
                        {t("common:cancel")}
                      </Button>
                      <Button variant="contained" onClick={saveVariant} disabled={savingVariant}>
                        {savingVariant
                          ? t("boShared:saving")
                          : editingVariantId || editingPendingVariantIndex != null
                            ? t("boProducts:saveVariant")
                            : t("boProducts:addVariant")}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    onClick={openAddVariant}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {t("boProducts:addVariant")}
                  </Button>
                )}
              </Stack>
          </Box>

          <AddImageByUrlDialog
            open={variantImageLinkDialogOpen}
            onClose={() => setVariantImageLinkDialogOpen(false)}
            onAdd={handleVariantImageFromUrl}
            busy={busyVariantId != null && busyVariantId === editingVariantId}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {currentProductId && !isEditing ? t("common:done") : t("common:cancel")}
        </Button>
        <Button variant="contained" disabled={saving || !name.trim() || !categoryId} onClick={handleSubmit}>
          {saving ? t("boShared:saving") : isEditing || currentProductId ? t("boProducts:saveChanges") : t("boProducts:createProduct")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
